import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useReducer,
  ReactNode,
} from 'react';

import { AssortmentSearch, Context, AssortmentFullInfoResponse } from './assortment.type';
import { apiClientJava } from '../../state';
import { AssortmentItemOptimized } from '../../types/models';

type AssortmentProps = {
  children: JSX.Element | ReactNode[] | null;
};

type State = Array<AssortmentItemOptimized>;

type Action =
  | { type: 'SET_ASSORTMENT_SEARCH'; payload: Array<AssortmentItemOptimized> }
  | { type: 'DELETE_ASSORTMENT_SEARCH' }
  | { type: 'UPDATE_ASSORTMENT_ITEMS'; payload: Array<AssortmentItemOptimized> };

const convertAssortmentSearch = (apiModel: AssortmentSearch): AssortmentItemOptimized => ({
  sku: apiModel.product_num,
  description: apiModel.product_english_desc || '',
  productEnglishLongDesc: apiModel.product_english_long_desc || '',
  corpStatus: apiModel.corporate_status_cd || '',
  storeStatus: '',
  totalCommitments: 0,
  dmoq: 0,
  primaryLocation: '',
  secondaryLocations: '',
  promotable: false,
  historic: 0,
  campsUnitAnnul: 0,
  lostSalesUnits: 0,
  lostSales$$: 0,
  histTotalSalesDollar: 0,
  totalForecast: 0,
  forecast: 0,
  ofPromo: 0,
  pctPromoHist: 0,
  dgm: 0,
  permanentCount: 0,

  isNewSku: false,
  opportunitySkuFlag: false,
  isExtendedAssortment: 'NO',

  avgReg: 0,
  avgRegIss: 0,
  avgRegPromo: 0,
  avgRegPromoIss: 0,
  peakReg: 0,
  peakRegIss: 0,
  peakRegPromo: 0,
  peakRegPromoIss: 0,

  facings: 0,
  q1f: 0,
  bincap: 0,
  fillTrips: 0,
  wos: 0,
  minTargetBincap: null,
  maxTargetBincap: null,

  crossMerch: 'NO',

  salesForecasts: {
    avgReg: 0,
    avgRegPromo: 0,
    avgRegIss: 0,
    avgRegPromoIss: 0,
    peakReg: 0,
    peakRegPromo: 0,
    peakRegIss: 0,
    peakRegPromoIss: 0,
  },
  partial: true,
});

function mergeFullInfoToAssortmentItem(
  partial: AssortmentItemOptimized,
  fullInfo: AssortmentFullInfoResponse
): AssortmentItemOptimized {
  const sku = partial.sku;

  const productInfo = fullInfo.productInfo.find((p) => p.product_num === sku);
  const forecastInfo = fullInfo.forecastInfo.find((f) => f.product_num === sku);
  const inventoryInfo = fullInfo.inventoryInfo.find((i) => i.product_num === sku);
  const locationInfo = fullInfo.locationInfo.find((l) => l.product_num === sku);

  const metrics = fullInfo.metricsProductStoreInfo;
  const bincap = metrics.bincap?.[sku] ?? partial.bincap;
  const facings = metrics.facings?.[sku] ?? partial.facings;
  const fillTrips = metrics.fill_trips?.[sku] ?? partial.fillTrips;
  const q1f = metrics.q1f?.[sku] ?? partial.q1f;
  const wos = metrics.wos?.[sku] ?? partial.wos;
  const totalCommitments = inventoryInfo?.total_item_count ??
    metrics.total_commitments?.[sku] ?? partial.totalCommitments;

  return {
    ...partial,
    description: productInfo?.product_english_desc ?? partial.description,
    productEnglishLongDesc: productInfo?.product_english_long_desc ?? partial.productEnglishLongDesc,
    corpStatus: productInfo?.corporate_status_cd ?? partial.corpStatus,
    dmoq: productInfo?.dmoq ?? partial.dmoq,
    primaryLocation: locationInfo?.primary_locations ?? partial.primaryLocation,
    secondaryLocations: locationInfo?.secondary_locations ?? partial.secondaryLocations,
    promotable: forecastInfo?.is_promotable ?? partial.promotable,
    historic: forecastInfo?.hist_total_sales ?? partial.historic,
    campsUnitAnnul: forecastInfo?.camps_sales ?? partial.campsUnitAnnul,
    lostSalesUnits: forecastInfo?.lost_sales_total ?? partial.lostSalesUnits,
    lostSales$$: forecastInfo?.lost_sales_total_dollar ?? partial.lostSales$$,
    histTotalSalesDollar: forecastInfo?.hist_total_sales_dollar ?? partial.histTotalSalesDollar,
    totalForecast: forecastInfo?.total_forecast ?? partial.totalForecast,
    forecast: forecastInfo?.total_forecast ?? partial.forecast,
    ofPromo: forecastInfo?.pct_promo_fcst ?? partial.ofPromo,
    pctPromoHist: forecastInfo?.pct_promo_hist ?? partial.pctPromoHist,
    dgm: inventoryInfo?.dgm_percentage ?? partial.dgm,
    storeStatus: inventoryInfo?.store_product_status ?? partial.storeStatus,
    permanentCount: locationInfo?.permanent_count ?? partial.permanentCount,
    isNewSku: forecastInfo?.is_new_sku ?? partial.isNewSku,
    opportunitySkuFlag: forecastInfo?.opportunity_sku_flag ?? partial.opportunitySkuFlag,
    isExtendedAssortment: productInfo
      ? productInfo.is_extended_assortment ? 'YES' : 'NO'
      : partial.isExtendedAssortment,
    avgReg: forecastInfo?.avg_reg_fcst ?? partial.avgReg,
    avgRegIss: forecastInfo?.avg_reg_iss_fcst ?? partial.avgRegIss,
    avgRegPromo: forecastInfo?.avg_reg_promo_fcst ?? partial.avgRegPromo,
    avgRegPromoIss: forecastInfo?.avg_reg_promo_iss_fcst ?? partial.avgRegPromoIss,
    peakReg: forecastInfo?.peak_reg_fcst ?? partial.peakReg,
    peakRegIss: forecastInfo?.peak_reg_iss_fcst ?? partial.peakRegIss,
    peakRegPromo: forecastInfo?.peak_reg_promo_fcst ?? partial.peakRegPromo,
    peakRegPromoIss: forecastInfo?.peak_reg_promo_iss_fcst ?? partial.peakRegPromoIss,
    facings,
    q1f,
    bincap,
    fillTrips,
    wos,
    totalCommitments,
    minTargetBincap: null,
    maxTargetBincap: null,
    crossMerch: 'NO',
    salesForecasts: {
      avgReg: forecastInfo?.avg_reg_fcst ?? partial.salesForecasts.avgReg,
      avgRegPromo: forecastInfo?.avg_reg_promo_fcst ?? partial.salesForecasts.avgRegPromo,
      avgRegIss: forecastInfo?.avg_reg_iss_fcst ?? partial.salesForecasts.avgRegIss,
      avgRegPromoIss: forecastInfo?.avg_reg_promo_iss_fcst ?? partial.salesForecasts.avgRegPromoIss,
      peakReg: forecastInfo?.peak_reg_fcst ?? partial.salesForecasts.peakReg,
      peakRegPromo: forecastInfo?.peak_reg_promo_fcst ?? partial.salesForecasts.peakRegPromo,
      peakRegIss: forecastInfo?.peak_reg_iss_fcst ?? partial.salesForecasts.peakRegIss,
      peakRegPromoIss: forecastInfo?.peak_reg_promo_iss_fcst ?? partial.salesForecasts.peakRegPromoIss,
    },
    partial: false,
    blockingAttributesInfo: fullInfo.blockingAttributesInfo
  };
}

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'SET_ASSORTMENT_SEARCH':
      return action.payload;
    case 'DELETE_ASSORTMENT_SEARCH':
      return [];
    case 'UPDATE_ASSORTMENT_ITEMS':
      const updated = action.payload;
      const updatedSkus = new Set(updated.map((item) => item.sku));

      return [
        ...state.filter((item) => !updatedSkus.has(item.sku)),
        ...updated,
      ];
    default:
      return state;
  }
}

export const AssortmentContext = createContext<Context>(null!);

const initState: State = [];

export function AssortmentProvider({ children }: AssortmentProps) {
  const [assortmentSearch, dispatch] = useReducer(reducer, initState);

  const getAssortmentSearch = useCallback(async (storeId: string, searchString: string) => {
    const res = await apiClientJava.assortmentSearch({ storeId, searchString });
    const converted = res.map((item) => convertAssortmentSearch(item));

    dispatch({
      type: 'SET_ASSORTMENT_SEARCH',
      payload: converted,
    });
  }, []);

  const getAssortmentBulkSearch = useCallback(async (storeId: string, skuList: string[]) => {
    const res = await apiClientJava.assortmentBulkSearch({ storeId, skuList });
    const converted = res.found_products?.map((item: AssortmentSearch) => convertAssortmentSearch(item));

    dispatch({
      type: 'SET_ASSORTMENT_SEARCH',
      payload: converted,
    });

    return { foundAssortments: converted, notFound: res.not_found_product_nums };
  }, []);

  const deleteAssortmentSearch = useCallback(() => {
    dispatch({ type: 'DELETE_ASSORTMENT_SEARCH' });
  }, []);

  const getAssortmentFullInfo = useCallback(
    async (storeId: string, aisleCode: string, skuList: string[]) => {

      if (!skuList.length) return;
      const res = await apiClientJava.getAssortmentFullInfo({
        storeId,
        aisleCode,
        skuList,
      });

      const updatedItems: AssortmentItemOptimized[] = skuList.map((sku) => {
        const partial = assortmentSearch.find((item) => item.sku === sku);

        if (!partial) return null;

        return mergeFullInfoToAssortmentItem(partial, res as unknown as AssortmentFullInfoResponse);
      }).filter(Boolean) as AssortmentItemOptimized[];

      dispatch({ type: 'UPDATE_ASSORTMENT_ITEMS', payload: updatedItems });
    },
    [assortmentSearch]
  );

  const memoedValue = useMemo(
    () => ({
      assortmentSearch,
      getAssortmentSearch,
      getAssortmentBulkSearch,
      deleteAssortmentSearch,
      getAssortmentFullInfo,
    }),
    [
      assortmentSearch,
      getAssortmentSearch,
      deleteAssortmentSearch,
      getAssortmentFullInfo,
    ],
  );

  return <AssortmentContext.Provider value={memoedValue}>{children}</AssortmentContext.Provider>;
}

const useAssortment = () => useContext(AssortmentContext);

export default useAssortment;
